import { useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import DocumentPreviewButtons from '@/components/DocumentPreviewButtons';
import { usePatientLabHistory, useParameterTrend, usePatientOutsourced } from './hooks';
import { LabStatusBadge, AbnormalFlag, fmtDateTime, fmtDate } from './ui';
import LabOrderDetails from './LabOrderDetails';

/**
 * A patient's laboratory record — every order, at every branch.
 *
 * TENANT-wide, deliberately (see queryKeys.ts's `patientLab`): a result taken
 * at one site is part of the record read at another, which is the branch
 * model's own stated intent. This is why the branch a test was done at is a
 * COLUMN here rather than a filter.
 *
 * Written as a standalone component rather than living inside the Laboratory
 * module's own tab, so the same view can be dropped into Patient Records and
 * the consultation screen without a second implementation — the same thing
 * features/prescriptions/PatientPrescriptionHistory.tsx already does.
 */
export interface PatientLabHistoryProps {
  uhid: string;
  /** Hides the trend tool where space is tight (e.g. an embedded panel). */
  compact?: boolean;
}

export default function PatientLabHistory({ uhid, compact = false }: PatientLabHistoryProps) {
  const { data, isLoading, error, refetch } = usePatientLabHistory(uhid);
  const [openOrderId, setOpenOrderId] = useState<number | null>(null);

  if (isLoading) return <LoadingState label="Loading laboratory history…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data || data.length === 0) {
    return <EmptyState label="No laboratory orders for this patient." hint="Orders raised from a visit or an admission appear here." />;
  }

  return (
    <div className="space-y-3">
      {!compact && <TrendPanel uhid={uhid} />}

      <OutsideLabResults uhid={uhid} />

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead className="text-right">Tests</TableHead>
              <TableHead>Findings</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((o) => (
              <TableRow key={o.id} className="cursor-pointer" onClick={() => setOpenOrderId(o.id)}>
                <TableCell className="font-mono text-[12px]">{o.order_number}</TableCell>
                <TableCell className="text-[12px]">{fmtDateTime(o.ordered_at)}</TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{o.branch_name ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{o.test_count}</TableCell>
                <TableCell>
                  {o.has_critical && <Badge variant="danger">Critical</Badge>}
                  {!o.has_critical && o.has_abnormal && <Badge variant="warn">Abnormal</Badge>}
                  {!o.has_critical && !o.has_abnormal && <span className="text-[12px] text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <LabStatusBadge status={o.status} />
                  {o.report_status && (
                    <Badge variant={o.report_status === 'amended' ? 'warn' : 'success'} className="ml-1">
                      {o.report_status === 'amended' ? `Report v${o.current_version}` : 'Report'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); setOpenOrderId(o.id); }}>
                    Open
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <LabOrderDetails orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
    </div>
  );
}

/**
 * Outside laboratory results — Patient → Medical Records → Laboratory →
 * Outside Laboratory → Vitamin D → result + original report.
 *
 * Values appear only once VERIFIED (the server withholds them before that):
 * a transcription nobody has signed for, let alone an OCR guess, is not a
 * result and must not read as one in a clinical record. The row still shows,
 * with its status, so a doctor can see the report is on its way.
 *
 * The original report opens through the ordinary patient-document route —
 * it IS an ordinary patient document.
 */
function OutsideLabResults({ uhid }: { uhid: string }) {
  const { data, isLoading, error } = usePatientOutsourced(uhid);
  if (isLoading || error || !data || data.length === 0) return null;

  return (
    <section className="space-y-1.5">
      <h3 className="text-sm font-semibold">Outside laboratory</h3>
      <div className="divide-y divide-border rounded-xl border border-border bg-card">
        {data.map((t) => {
          const final = t.status === 'VERIFIED' || t.status === 'REPORTED';
          return (
            <div key={t.order_item_id} className="space-y-1 px-3 py-2 text-[12px]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="text-[13px] font-semibold">{t.test_name}</span>
                  <span className="text-muted-foreground">
                    {' '}· {t.reference_lab_name ?? 'Reference lab'} · {t.order_number} · ordered {fmtDate(t.ordered_at)}
                    {t.report_date ? ` · report dated ${fmtDate(t.report_date)}` : ''}
                  </span>
                </div>
                <span className="flex items-center gap-1.5">
                  {final && t.has_critical && <Badge variant="danger">Critical</Badge>}
                  {final && !t.has_critical && t.has_abnormal && <Badge variant="warn">Abnormal</Badge>}
                  <LabStatusBadge status={t.status} processingType="outsourced" />
                  {t.document_id && final && (
                    <DocumentPreviewButtons uhid={uhid} docId={t.document_id} label="Original lab report" className="inline-flex gap-1" />
                  )}
                </span>
              </div>
              {final && t.values.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                  {t.values.map((v, i) => (
                    <span key={i}>
                      {v.parameter_name}: <span className="font-mono font-semibold">{v.value_text ?? v.value_numeric}</span> {v.unit ?? ''}
                      {v.reference_display ? <span className="text-muted-foreground"> (ref {v.reference_display})</span> : null}{' '}
                      <AbnormalFlag flag={v.flag} compact />
                    </span>
                  ))}
                </div>
              )}
              {final && t.values.length === 0 && t.result_mode === 'report_only' && (
                <div className="text-muted-foreground">Result is in the attached report (not transcribed).</div>
              )}
              {!final && <div className="text-muted-foreground">Not yet verified — no result to show.</div>}
              {final && t.verified_by && (
                <div className="text-muted-foreground">
                  Verified by {t.verified_by} · {fmtDateTime(t.verified_at)}
                  {t.finalized_by ? ` · finalized by ${t.finalized_by}` : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Parameter comparison over time — "HbA1c: 7.8 → 7.2 → 6.9".
 *
 * Only VERIFIED values are plotted: an unverified number is not yet a result,
 * and putting one on the same axis as ones somebody has signed for would make
 * them look equally settled. The server enforces that; this just says so.
 *
 * Looked up by parameter CODE rather than id, so a trend spans a test that
 * was reconfigured and two tests that both report the same analyte.
 */
function TrendPanel({ uhid }: { uhid: string }) {
  const [code, setCode] = useState('');
  const [active, setActive] = useState<string | null>(null);
  const { data, isLoading, error } = useParameterTrend(uhid, active);

  const points = data ?? [];
  const numeric = points.filter((p) => p.value_numeric !== null);
  const values = numeric.map((p) => Number(p.value_numeric));
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const span = max - min || 1;

  return (
    <div className="rounded-xl border border-border p-3">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); setActive(code.trim().toUpperCase() || null); }}
      >
        <label className="text-[12px]">
          <span className="mb-1 block text-muted-foreground">Compare a parameter over time</span>
          <Input
            className="w-[200px]"
            placeholder="Parameter code, e.g. HB"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        <Button type="submit" size="sm">Show trend</Button>
      </form>

      {active && isLoading && <LoadingState className="mt-2" label="Loading…" />}
      {active && error && <ErrorState className="mt-2" message={(error as Error).message} />}
      {active && !isLoading && points.length === 0 && (
        <p className="mt-2 text-[12px] text-muted-foreground">
          No verified results for “{active}” in this patient’s record.
        </p>
      )}

      {points.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-[13px] font-semibold">
            {points[points.length - 1]?.parameter_name}
            <span className="ml-2 text-[11px] font-normal text-muted-foreground">
              {points[points.length - 1]?.unit ?? ''} · reference {points[points.length - 1]?.reference_display ?? '—'}
            </span>
          </div>

          {/*
            A sparkline in plain divs rather than a charting library: this is
            three to ten points and the shape is the whole message. The numbers
            below it are what a clinician actually reads — the bars are a hint,
            not the data.
          */}
          {numeric.length > 1 && (
            <div className="mb-2 flex h-16 items-end gap-1">
              {numeric.map((p, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-t ${
                    p.flag === 'CRITICAL_LOW' || p.flag === 'CRITICAL_HIGH' ? 'bg-danger'
                      : p.flag === 'NORMAL' ? 'bg-teal' : 'bg-warn'
                  }`}
                  style={{ height: `${8 + ((Number(p.value_numeric) - min) / span) * 92}%` }}
                  title={`${p.value_numeric} ${p.unit ?? ''} — ${fmtDate(p.verified_at)}`}
                />
              ))}
            </div>
          )}

          <div className="divide-y divide-border rounded-lg border border-border">
            {[...points].reverse().map((p, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-1.5 text-[12px]">
                <span className="text-muted-foreground">{fmtDate(p.verified_at)} · {p.test_name}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono font-semibold">
                    {p.value_text ?? p.value_numeric} {p.unit ?? ''}
                  </span>
                  <AbnormalFlag flag={p.flag} compact />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
